import { htmlNodeAtPath } from './htmlTexts';
import { MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from './PageBuilderConstants';

const queries = {
    desktop: `(min-width: ${TABLET_BREAKPOINT + 1}px)`,
    tablet: `(min-width: ${MOBILE_BREAKPOINT + 1}px) and (max-width: ${TABLET_BREAKPOINT}px)`,
    mobile: `(max-width: ${MOBILE_BREAKPOINT}px)`,
};
const properties = ['font-family', 'font-size', 'font-weight'];
const parse = html => { const template = document.createElement('template'); template.innerHTML = html || ''; return template; };
const escapeClass = value => Array.from(value).map(char => /[a-zA-Z0-9_-]/.test(char) ? char : `\\${char.codePointAt(0).toString(16)} `).join('').replace(/^(\d)/, '\\3$1 ');

export function getTypographyClasses(html, path) {
    return Array.from(htmlNodeAtPath(parse(html).content, path)?.classList || []);
}

export function getHtmlTypographyTarget(html, path) {
    return htmlNodeAtPath(parse(html).content, path)?.getAttribute('data-builder-typography-target') || '';
}

export function updateElementTypography(html, path, values, view, allViews = false, scope = '') {
    const template = parse(html);
    const element = htmlNodeAtPath(template.content, path);
    if (!element?.classList) return html;
    let target = element.getAttribute('data-builder-typography-target');
    if (!target || !element.classList.contains(target)) {
        const prefix = `html-element-${scope.replace(/[^a-zA-Z0-9_-]/g, '') || 'text'}`;
        let index = 1;
        const classes = new Set(Array.from(template.content.querySelectorAll('[class]')).flatMap(node => Array.from(node.classList)));
        while (classes.has(`${prefix}-${index}`)) index += 1;
        target = `${prefix}-${index}`;
        element.classList.add(target);
        element.setAttribute('data-builder-typography-target', target);
    }
    return updateHtmlTypography(template.innerHTML, path, target, values, view, allViews, scope);
}

export function getHtmlTypography(html, className, view) {
    const style = Array.from(parse(html).content.querySelectorAll('style[data-builder-typography]'))
        .find(node => node.dataset.builderTypography === className && node.dataset.view === view);
    const declarations = document.createElement('span').style;
    declarations.cssText = style?.textContent.match(/\{[^{}]*\{([^{}]*)\}/)?.[1] || '';
    return Object.fromEntries(properties.map(property => [property, declarations.getPropertyValue(property)])
        .filter(([, value]) => value));
}

export function updateHtmlTypography(html, path, className, values, view, allViews = false, scope = '') {
    const template = parse(html);
    const element = htmlNodeAtPath(template.content, path);
    if (!element?.classList || !queries[view]) return html;
    const declarations = properties.filter(property => values[property]?.trim()).map(property => {
        const value = values[property].trim();
        // Values are declarations, never arbitrary CSS or markup.
        if (/[;{}<>]/.test(value)) throw new Error('Inserisci un valore CSS valido.');
        if (typeof CSS !== 'undefined' && CSS.supports && !CSS.supports(property, value)) throw new Error(`Valore non valido per ${property}.`);
        return `${property}: ${value} !important;`;
    });
    if (!declarations.length) return html;
    let selectedClass = className;
    if (!selectedClass) {
        let index = 1;
        const prefix = `html-text${scope ? `-${scope.replace(/[^a-zA-Z0-9_-]/g, '')}` : ''}`;
        const classes = new Set(Array.from(template.content.querySelectorAll('[class]')).flatMap(node => Array.from(node.classList)));
        while (classes.has(`${prefix}-${index}`)) index += 1;
        selectedClass = `${prefix}-${index}`;
        element.classList.add(selectedClass);
    }
    if (!element.classList.contains(selectedClass)) return html;
    const views = allViews ? Object.keys(queries) : [view];
    views.forEach(targetView => {
        let style = Array.from(template.content.querySelectorAll('style[data-builder-typography]')).find(node => node.dataset.builderTypography === selectedClass && node.dataset.view === targetView);
        if (!style) {
            style = document.createElement('style');
            style.dataset.builderTypography = selectedClass;
            style.dataset.view = targetView;
            template.content.appendChild(style);
        }
        // Keep unrelated typography properties when editing only one control.
        const previous = style.textContent.match(/\{[^{}]*\{([^{}]*)\}/)?.[1] || '';
        const merged = document.createElement('span').style;
        merged.cssText = previous;
        properties.forEach(property => { if (values[property]?.trim()) merged.setProperty(property, values[property].trim(), 'important'); });
        template.content.appendChild(style);
        style.textContent = `@media ${queries[targetView]} {\n  .${escapeClass(selectedClass)} { ${merged.cssText} }\n}`;
    });
    return template.innerHTML;
}

// The canvas view is independent from the browser window. Keep the exported
// media queries intact, but activate only the selected view inside the editor.
export function htmlTypographyPreview(html, view) {
    if (!html?.includes('data-builder-typography')) return html;
    const template = parse(html);
    template.content.querySelectorAll('style[data-builder-typography]').forEach(style => {
        style.textContent = style.dataset.view === view
            ? style.textContent.replace(/^@media[^{}]+\{\s*/, '').replace(/\s*}\s*$/, '')
            : '';
    });
    return template.innerHTML;
}
